import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { ENTITY_LOG_KEY } from '@/shared/hooks/use-workflows'

/**
 * Property groups: a named group with a mailing address and contacts,
 * holding the tenant's properties. Served by /api/registrations/groups/.
 */

/** Flat address shape the group endpoints read and return. */
export interface GroupAddress {
  line1: string
  line2: string
  city: string
  state: string
  zip_code: string
  country: string
}

export interface GroupContact {
  uuid: string
  first_name: string
  last_name: string
  email: string
  phone: string
  is_primary: boolean
}

export type GroupContactInput = Omit<GroupContact, 'uuid' | 'is_primary'>

export interface PropertyGroup {
  uuid: string
  name: string
  mailing_address: GroupAddress
  mailing_address_display: string
  contacts: GroupContact[]
  property_count: number
  /** The tenant's property types (structure options), served as data. */
  property_type_options: { value: string; label: string }[]
  /** The property type a new property in this group starts with. */
  default_property_type?: string
  /** Import target for one-file grouped claims, or null when not set up. */
  claim_upload_target: string | null
  created_at: string | null
}

export interface GroupPropertyRow {
  uuid: string
  address: string
  owner_name: string
  registered_products: number
  open_claims: number
  open_transfers: number
  registrations: { uuid: string; registration_number: string; product: string }[]
  added_at: string | null
}

export interface GroupPropertyCreateInput {
  address: GroupAddress
  owner: GroupContactInput
  mailing_address?: GroupAddress | null
  property_type?: string
  co_owner?: GroupContactInput | null
}

const BASE = '/registrations/groups/'
const unwrap = <T,>(r: { data: { data?: T } & T }): T =>
  (r.data?.data ?? r.data) as T

export const groupKeys = {
  all: ['property-groups'] as const,
  detail: (uuid: string) => ['property-groups', uuid] as const,
  properties: (uuid: string) => ['property-groups', uuid, 'properties'] as const,
}

export function usePropertyGroup(uuid: string | undefined) {
  return useQuery<PropertyGroup>({
    queryKey: groupKeys.detail(uuid ?? ''),
    queryFn: () => api.get(`${BASE}${uuid}/`).then(unwrap<PropertyGroup>),
    enabled: !!uuid,
  })
}

export function useGroupProperties(uuid: string | undefined) {
  return useQuery<GroupPropertyRow[]>({
    queryKey: groupKeys.properties(uuid ?? ''),
    queryFn: () =>
      api.get(`${BASE}${uuid}/properties/`).then(unwrap<GroupPropertyRow[]>),
    enabled: !!uuid,
  })
}

/** Refresh everything shown about a group (e.g. after an import lands). */
export function useRefreshGroup(uuid: string | undefined) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: groupKeys.all })
    void qc.invalidateQueries({ queryKey: ENTITY_LOG_KEY })
    if (uuid) void qc.invalidateQueries({ queryKey: groupKeys.detail(uuid) })
  }
}

export function useCreatePropertyGroup() {
  const invalidate = useRefreshGroup(undefined)
  return useMutation<
    PropertyGroup,
    Error,
    { name: string; mailing_address: GroupAddress; contacts: GroupContactInput[] }
  >({
    mutationFn: (data) => api.post(BASE, data).then(unwrap<PropertyGroup>),
    onSuccess: invalidate,
  })
}

export function useUpdatePropertyGroup(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<
    PropertyGroup,
    Error,
    { name?: string; mailing_address?: GroupAddress }
  >({
    mutationFn: (data) =>
      api.patch(`${BASE}${uuid}/`, data).then(unwrap<PropertyGroup>),
    onSuccess: invalidate,
  })
}

export function useAddGroupContact(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<PropertyGroup, Error, GroupContactInput>({
    mutationFn: (data) =>
      api.post(`${BASE}${uuid}/contacts/`, data).then(unwrap<PropertyGroup>),
    onSuccess: invalidate,
  })
}

export function useUpdateGroupContact(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<
    PropertyGroup,
    Error,
    { contactUuid: string; data: GroupContactInput }
  >({
    mutationFn: ({ contactUuid, data }) =>
      api
        .patch(`${BASE}${uuid}/contacts/${contactUuid}/`, data)
        .then(unwrap<PropertyGroup>),
    onSuccess: invalidate,
  })
}

export function useRemoveGroupContact(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<PropertyGroup, Error, string>({
    mutationFn: (contactUuid) =>
      api
        .delete(`${BASE}${uuid}/contacts/${contactUuid}/`)
        .then(unwrap<PropertyGroup>),
    onSuccess: invalidate,
  })
}

export function useAddGroupProperty(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<{ uuid: string; address: string }, Error, string>({
    mutationFn: (propertyUuid) =>
      api
        .post(`${BASE}${uuid}/properties/add/`, { property_uuid: propertyUuid })
        .then(unwrap<{ uuid: string; address: string }>),
    onSuccess: invalidate,
  })
}

export function useRemoveGroupProperty(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<{ removed: boolean }, Error, string>({
    mutationFn: (propertyUuid) =>
      api
        .post(`${BASE}${uuid}/properties/remove/`, { property_uuid: propertyUuid })
        .then(unwrap<{ removed: boolean }>),
    onSuccess: invalidate,
  })
}

export interface CopyRegistrationResponse {
  results: {
    property_uuid: string
    address?: string
    status?: 'registered' | 'already_registered'
    registration_number?: string
    error?: string
  }[]
  registered: number
  already_registered: number
  errors: number
}

export function useCopyRegistration(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  const qc = useQueryClient()
  return useMutation<
    CopyRegistrationResponse,
    Error,
    { registration_uuid: string; property_uuids: string[] }
  >({
    mutationFn: (data) =>
      api
        .post(`${BASE}${uuid}/copy-registration/`, data)
        .then(unwrap<CopyRegistrationResponse>),
    onSuccess: () => {
      invalidate()
      void qc.invalidateQueries({ queryKey: ['property'] })
    },
  })
}

/** One row per group for the landing page: counts and who to call. */
export interface GroupSummary {
  uuid: string
  name: string
  mailing_address_display: string
  contact_name: string
  contact_email: string
  contact_phone: string
  property_count: number
  registered_products: number
  open_claims: number
  open_transfers: number
  created_at: string | null
  /** The bulk claim upload target for group claims, when the tenant has one. */
  claim_upload_target?: string | null
}

export function useGroupSummaries() {
  return useQuery<GroupSummary[]>({
    queryKey: [...groupKeys.all, 'summaries'],
    queryFn: () => api.get(BASE).then(unwrap<GroupSummary[]>),
  })
}

export interface GroupClaimRow {
  uuid: string
  claim_number: string
  /** "group": one claim covering several properties; "single": one property. */
  kind: 'group' | 'single'
  properties: number
  address: string
  status_label: string
  is_open: boolean
  created_at: string | null
}

export function useGroupClaims(uuid: string | undefined) {
  return useQuery<GroupClaimRow[]>({
    queryKey: [...groupKeys.detail(uuid ?? ''), 'claims'],
    queryFn: () => api.get(`${BASE}${uuid}/claims/`).then(unwrap<GroupClaimRow[]>),
    enabled: !!uuid,
  })
}

export interface GroupRegistrationRow {
  uuid: string
  registration_number: string
  property_uuid: string
  property: string
  product: string
  warranty_class: string
  status_label: string
  is_active: boolean
  /** Active (registered), so it can back a claim; a Draft one cannot. */
  can_claim: boolean
  registered_at: string | null
}

/** Every registration on the group's properties. */
export function useGroupRegistrations(uuid: string | undefined) {
  return useQuery<GroupRegistrationRow[]>({
    queryKey: [...groupKeys.detail(uuid ?? ''), 'registrations'],
    queryFn: () =>
      api.get(`${BASE}${uuid}/registrations/`).then(unwrap<GroupRegistrationRow[]>),
    enabled: !!uuid,
  })
}

export interface GroupTransferRow {
  uuid: string
  reference: string
  property_uuid: string
  property: string
  status_label: string
  is_open: boolean
  updated_at: string | null
}

/** Every warranty transfer on the group's properties. */
export function useGroupTransfers(uuid: string | undefined) {
  return useQuery<GroupTransferRow[]>({
    queryKey: [...groupKeys.detail(uuid ?? ''), 'transfers'],
    queryFn: () => api.get(`${BASE}${uuid}/transfers/`).then(unwrap<GroupTransferRow[]>),
    enabled: !!uuid,
  })
}

/** Open one group claim by hand for the chosen registrations. */
export function useCreateGroupClaim(uuid: string) {
  const invalidate = useRefreshGroup(uuid)
  return useMutation<
    { uuid: string; claim_number: string; lines: number },
    Error,
    { registrations: string[]; description: string }
  >({
    mutationFn: (data) =>
      api
        .post(`${BASE}${uuid}/claims/new/`, data)
        .then(unwrap<{ uuid: string; claim_number: string; lines: number }>),
    onSuccess: invalidate,
  })
}
